"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  type ReactNode,
} from "react";
import type { StudioAsset } from "@/puck/types";
import type { StudioUploadPurpose } from "@/utils/studioBlobPath";
import { uploadStudioFileFromClient } from "@/utils/studioClientUpload";
import { deleteStudioAssetFromClient } from "@/utils/studioClientDelete";
import {
  mergeStudioAsset,
  removeStudioAssetByUrl,
  studioAssetFromUploadResponse,
} from "@/utils/studioAssets";
import {
  collectStudioAssetUrlsInUse,
  countStudioAssetUrlReferences,
} from "@/utils/studioAssetUsage";

type StudioFileAssetsContextValue = {
  pageId: string;
  assets: StudioAsset[];
  urlsInUse: ReadonlySet<string>;
  uploadFile: (
    file: File,
    purpose?: StudioUploadPurpose,
  ) => Promise<StudioAsset>;
  addAsset: (asset: StudioAsset) => void;
  removeAsset: (url: string) => Promise<void>;
  countReferences: (url: string) => number;
};

const StudioFileAssetsContext =
  createContext<StudioFileAssetsContextValue | null>(null);

export function useStudioFileAssets(): StudioFileAssetsContextValue {
  const context = useContext(StudioFileAssetsContext);
  if (!context) {
    throw new Error(
      "useStudioFileAssets must be used within StudioFileAssetsProvider",
    );
  }
  return context;
}

type StudioFileAssetsProviderProps = {
  pageId: string;
  assets: StudioAsset[];
  /** Draft/published Puck data + meta used to detect which assets are referenced. */
  contentRoots: unknown[];
  onAssetsChange: (
    assets: StudioAsset[] | ((prev: StudioAsset[]) => StudioAsset[]),
  ) => void;
  children: ReactNode;
};

export function StudioFileAssetsProvider({
  pageId,
  assets,
  contentRoots,
  onAssetsChange,
  children,
}: Readonly<StudioFileAssetsProviderProps>) {
  const urlsInUse = useMemo(
    () => collectStudioAssetUrlsInUse(contentRoots),
    [contentRoots],
  );

  const countReferences = useCallback(
    (url: string) => countStudioAssetUrlReferences(url, contentRoots),
    [contentRoots],
  );

  const addAsset = useCallback(
    (asset: StudioAsset) => {
      onAssetsChange((prev) => mergeStudioAsset(prev, asset));
    },
    [onAssetsChange],
  );

  const uploadFile = useCallback(
    async (file: File, purpose: StudioUploadPurpose = "page-file") => {
      const payload = await uploadStudioFileFromClient(
        pageId,
        file,
        undefined,
        purpose,
      );

      const asset = studioAssetFromUploadResponse({
        resourceId: payload.resourceId,
        url: payload.url,
        filename: payload.filename,
      });
      addAsset(asset);
      return asset;
    },
    [addAsset, pageId],
  );

  const removeAsset = useCallback(
    async (url: string) => {
      await deleteStudioAssetFromClient(url);
      onAssetsChange((prev) => removeStudioAssetByUrl(prev, url));
    },
    [onAssetsChange],
  );

  const value = useMemo(
    () => ({
      pageId,
      assets,
      urlsInUse,
      uploadFile,
      addAsset,
      removeAsset,
      countReferences,
    }),
    [
      addAsset,
      assets,
      countReferences,
      pageId,
      removeAsset,
      uploadFile,
      urlsInUse,
    ],
  );

  return (
    <StudioFileAssetsContext.Provider value={value}>
      {children}
    </StudioFileAssetsContext.Provider>
  );
}
